import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FiDownload, FiUsers, FiChevronRight, FiChevronDown, FiSearch, FiEye, FiRefreshCw, FiCalendar } from 'react-icons/fi';
import SummaryApi from '../common';
import * as XLSX from 'xlsx';
import Modal from 'react-modal';

// ────────────────────────────────────────────────
// THEME — same rules as other files
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionBlue: '#2563EB',
  actionBlueHover: '#1D4ED8',
  actionGreen: '#10B981',
  actionGreenHover: '#059669',
  actionRed: '#EF4444',
  actionRedHover: '#DC2626',
};

const TeamleadExcelPromotionDataReport = () => {
  const [employeesList, setEmployeesList] = useState([]);
  const [filteredEmployeesList, setFilteredEmployeesList] = useState([]);
  const [memberType, setMemberType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedEmployeePromotions, setSelectedEmployeePromotions] = useState({});
  const [promotionLoading, setPromotionLoading] = useState({});
  const [employeeStatsLoading, setEmployeeStatsLoading] = useState({});
  const [employeeStats, setEmployeeStats] = useState({});
  const [expandedFeedback, setExpandedFeedback] = useState({});

  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [selectedDate, setSelectedDate] = useState('');

  const [statusFilter, setStatusFilter] = useState("ALL");

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  const normalizeStatus = (status) => {
    if (!status) return 'N/A';
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === 'delivered') return 'Delivered';
    if (lowerStatus === 'read') return 'Read';
    return status;
  };

  const getStatusStyle = (status) => {
    if (!status) return 'bg-gray-100 text-gray-600';
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === 'delivered') return 'bg-green-100 text-green-700';
    if (lowerStatus === 'read') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-600';
  };

  const fetchMemberType = () => {
    const encodedMemberType = sessionStorage.getItem('K3M8X1JQ5VZ4B7PWTL9');
    const memberType = encodedMemberType ? atob(encodedMemberType) : null;
    setMemberType(memberType);
    return memberType;
  };

  useEffect(() => {
    fetchMemberType();
    fetchTeamLeadEmployeeData();
  }, []);

  const getFilterDate = () => {
    if (dateFilter === 'today') {
      const today = new Date();
      return today.toISOString().split('T')[0];
    } else if (dateFilter === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday.toISOString().split('T')[0];
    } else if (dateFilter === 'custom' && selectedDate) {
      return selectedDate;
    }
    return null;
  };

  useEffect(() => {
    let filtered = [...employeesList];

    if (searchTerm) {
      filtered = filtered.filter(emp =>
        emp.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employee_mobile_number?.includes(searchTerm) ||
        emp.mobile_number?.includes(searchTerm) ||
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredEmployeesList(filtered);
  }, [searchTerm, employeesList]);

  const fetchTeamLeadEmployeeData = async () => {
    setLoading(true);
    try {
      const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
      if (!encodedEmployeeId) {
        toast.error('Employee ID not found in session storage');
        setLoading(false);
        return;
      }
      const decodedEmployeeId = atob(encodedEmployeeId);

      const response = await fetch(SummaryApi.getteamleademployeedata.url, {
        method: SummaryApi.getteamleademployeedata.method,
        headers,
        body: JSON.stringify({ team_lead_emp_id: decodedEmployeeId }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch employee data');
      }

      const data = await response.json();
      const employees = data?.data || [];
      setEmployeesList(employees);
      setFilteredEmployeesList(employees);

      const filterDate = getFilterDate();
      if (filterDate) {
        await fetchAllEmployeesStats(employees, filterDate);
      }
    } catch (error) {
      toast.error(`Error fetching employees: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEmployeesStats = async (employees, filterDate) => {
    for (const employee of employees) {
      await fetchEmployeeStats(employee.employee_id, filterDate, employee.employee_name);
    }
  };

  const fetchEmployeeStats = async (employeeId, filterDate, employeeName) => {
    setEmployeeStatsLoading(prev => ({ ...prev, [employeeId]: true }));
    try {
      const response = await fetch(SummaryApi.getPromotionStudentExcelData.url, {
        method: SummaryApi.getPromotionStudentExcelData.method,
        headers,
        body: JSON.stringify({
          employee_id: employeeId,
          promotion_date: filterDate
        }),
      });

      if (!response.ok) throw new Error('Failed to fetch employee stats');

      const data = await response.json();
      setEmployeeStats(prev => ({
        ...prev,
        [employeeId]: {
          delivered_count: data?.delivered_count || 0,
          read_count: data?.read_count || 0
        }
      }));
    } catch (error) {
      console.error(`Error fetching stats for ${employeeName}:`, error);
      setEmployeeStats(prev => ({
        ...prev,
        [employeeId]: { delivered_count: 0, read_count: 0 }
      }));
    } finally {
      setEmployeeStatsLoading(prev => ({ ...prev, [employeeId]: false }));
    }
  };

  const fetchPromotionDataForEmployee = async (employeeId, employeeName) => {
    setPromotionLoading(prev => ({ ...prev, [employeeId]: true }));
    try {
      const filterDate = getFilterDate();

      const response = await fetch(SummaryApi.getPromotionStudentExcelData.url, {
        method: SummaryApi.getPromotionStudentExcelData.method,
        headers,
        body: JSON.stringify({
          employee_id: employeeId,
          promotion_date: filterDate
        }),
      });

      if (!response.ok) throw new Error('Failed to fetch promotion data');

      const data = await response.json();
      const promotions = data?.data || [];

      setEmployeeStats(prev => ({
        ...prev,
        [employeeId]: {
          delivered_count: data?.delivered_count || 0,
          read_count: data?.read_count || 0
        }
      }));

      setSelectedEmployeePromotions(prev => ({ ...prev, [employeeId]: promotions }));
    } catch (error) {
      toast.error(`Error fetching promotion data: ${error.message}`);
      setSelectedEmployeePromotions(prev => ({ ...prev, [employeeId]: [] }));
    } finally {
      setPromotionLoading(prev => ({ ...prev, [employeeId]: false }));
    }
  };

  const toggleRowExpand = (employeeId, employeeName) => {
    const isExpanded = !expandedRows[employeeId];
    setExpandedRows(prev => ({ ...prev, [employeeId]: isExpanded }));

    if (isExpanded) {
      setStatusFilter("ALL");
    }

    if (isExpanded && !selectedEmployeePromotions[employeeId]) {
      fetchPromotionDataForEmployee(employeeId, employeeName);
    }
  };

  const handleDateFilterChange = async (value) => {
    setDateFilter(value);
    if (value !== 'custom') setSelectedDate('');

    setSelectedEmployeePromotions({});
    setExpandedRows({});
    setEmployeeStats({});
    setStatusFilter("ALL");

    const filterDate = value === 'custom' ? selectedDate :
      value === 'today' ? new Date().toISOString().split('T')[0] :
        value === 'yesterday' ? new Date(Date.now() - 86400000).toISOString().split('T')[0] : null;

    if (filterDate && employeesList.length > 0) {
      await fetchAllEmployeesStats(employeesList, filterDate);
    }
  };

  const handleCustomDateChange = async (date) => {
    setSelectedDate(date);
    if (dateFilter === 'custom' && date) {
      setSelectedEmployeePromotions({});
      setExpandedRows({});
      setEmployeeStats({});
      setStatusFilter("ALL");
      if (employeesList.length > 0) {
        await fetchAllEmployeesStats(employeesList, date);
      }
    }
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedStudent(null);
  };

  const exportToExcel = (promotions, employeeName) => {
    if (!promotions || promotions.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const excelData = promotions.map((promo, index) => ({
      'S.No': index + 1,
      'Student Name': promo.student_name || '',
      'Mobile Number': promo.mobile_number || '',
      'Status': normalizeStatus(promo.status),
      'Promotion Date': formatDate(promo.promotion_date),
      'Created Date': formatDate(promo.created_date),
      'Feedback': promo.feedback || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Promotion Data');

    const colWidths = [
      { wch: 6 }, { wch: 25 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 40 }
    ];
    worksheet['!cols'] = colWidths;

    const fileName = `Promotion_Data_${employeeName}_${getFilterDisplayText()}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    toast.success('Excel report downloaded successfully');
  };

  const exportAllToExcel = () => {
    const allPromotions = [];

    Object.keys(selectedEmployeePromotions).forEach(employeeId => {
      const promotions = selectedEmployeePromotions[employeeId];
      const employee = employeesList.find(emp => emp.employee_id === employeeId);
      const employeeName = employee?.employee_name || 'Unknown';

      promotions.forEach((promo) => {
        allPromotions.push({
          'Employee Name': employeeName,
          'Student Name': promo.student_name || '',
          'Mobile Number': promo.mobile_number || '',
          'Status': normalizeStatus(promo.status),
          'Promotion Date': formatDate(promo.promotion_date),
          'Created Date': formatDate(promo.created_date),
          'Feedback': promo.feedback || '',
        });
      });
    });

    if (allPromotions.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(allPromotions);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'All Promotion Data');

    const colWidths = [
      { wch: 20 }, { wch: 25 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 40 }
    ];
    worksheet['!cols'] = colWidths;

    const fileName = `All_Promotion_Data_${getFilterDisplayText()}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    toast.success('All promotion data exported successfully');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date)) return 'N/A';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateFilter('today');
    setSelectedDate('');
    setSelectedEmployeePromotions({});
    setExpandedRows({});
    setEmployeeStats({});
    setStatusFilter("ALL");
    toast.info('Filters cleared');
    const today = new Date().toISOString().split('T')[0];
    if (employeesList.length > 0) {
      fetchAllEmployeesStats(employeesList, today);
    }
  };

  const getFilterDisplayText = () => {
    if (dateFilter === 'today') return 'Today';
    if (dateFilter === 'yesterday') return 'Yesterday';
    if (dateFilter === 'custom' && selectedDate) return formatDate(selectedDate);
    return 'Today';
  };

  Modal.setAppElement('#root');

  const PromotionTable = ({ employeeId, employeeName, promotions, isLoading }) => {
    const filteredData = useMemo(() => {
      if (statusFilter === "ALL") return promotions;
      return promotions.filter((row) => normalizeStatus(row.status) === statusFilter);
    }, [statusFilter, promotions]);

    const deliveredCount = promotions.filter(p => normalizeStatus(p.status) === "Delivered").length;
    const readCount = promotions.filter(p => normalizeStatus(p.status) === "Read").length;
    const totalCount = promotions.length;

    if (isLoading) {
      return (
        <div className="text-center py-4">
          <div
            className="animate-spin rounded-full h-5 w-5 border-b-2 mx-auto"
            style={{ borderColor: THEME.primary }}
          ></div>
          <p className="text-gray-500 text-xs mt-1">Loading...</p>
        </div>
      );
    }

    if (promotions.length === 0) {
      return (
        <div className="text-center py-4 text-gray-500 text-xs">
          <p>No promotion data found for {getFilterDisplayText()}</p>
        </div>
      );
    }

    return (
      <div>
        {/* Filter Badges */}
        <div className="flex gap-3 mb-3 pb-2 border-b">
          <span
            className={`px-2 py-1 rounded-full text-xs cursor-pointer hover:opacity-80 transition text-white ${statusFilter === "ALL" ? "bg-gray-700" : "bg-gray-400"}`}
            onClick={() => setStatusFilter("ALL")}
          >
            All ({totalCount})
          </span>
          <span
            className={`px-2 py-1 rounded-full text-xs cursor-pointer hover:opacity-80 transition text-white ${statusFilter === "Delivered" ? "bg-green-600" : "bg-green-400"}`}
            onClick={() => setStatusFilter("Delivered")}
          >
            Delivered ({deliveredCount})
          </span>
          <span
            className={`px-2 py-1 rounded-full text-xs cursor-pointer hover:opacity-80 transition text-white ${statusFilter === "Read" ? "bg-yellow-600" : "bg-yellow-400"}`}
            onClick={() => setStatusFilter("Read")}
          >
            Read ({readCount})
          </span>
          {statusFilter !== "ALL" && (
            <button
              onClick={() => setStatusFilter("ALL")}
              style={{ color: THEME.actionRed }}
              className="text-xs ml-auto font-semibold hover:underline"
            >
              Clear filter
            </button>
          )}
        </div>

        {/* Filtered Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-200">
              <tr>
                <th className="px-2 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>S.No</th>
                <th className="px-2 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Student Name</th>
                <th className="px-2 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Mobile</th>
                <th className="px-2 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Status</th>
                <th className="px-2 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Promotion date</th>
                <th className="px-2 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Feedback</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((promo, promoIdx) => (
                <tr key={promoIdx} className="border-b border-gray-100 hover:bg-slate-50">
                  <td className="px-2 py-1 text-gray-600">{promoIdx + 1}</td>
                  <td className="px-2 py-1 text-gray-800 font-medium">{promo.student_name || 'N/A'}</td>
                  <td className="px-2 py-1 text-gray-600">{promo.mobile_number || 'N/A'}</td>
                  <td className="px-2 py-1">
                    <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-semibold ${getStatusStyle(promo.status)}`}>
                      {normalizeStatus(promo.status)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-gray-600">{promo.promotion_date}</td>
                  <td className="px-2 py-1 text-gray-700">
                    {promo.feedback ? (
                      <div>
                        <p className="text-xs text-gray-700">
                          {promo.feedback.length > 40
                            ? promo.feedback.slice(0, 40) + '...'
                            : promo.feedback}
                        </p>
                        {promo.feedback.length > 40 && (
                          <button
                            onClick={() => handleViewStudent(promo)}
                            style={{ color: THEME.actionBlue }}
                            className="text-xs mt-1 hover:underline font-semibold"
                          >
                            View more
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">No feedback</span>
                    )}
                  </td>
                </tr>
              ))}
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-gray-500">
                    No {statusFilter !== "ALL" ? statusFilter : ""} records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-xl shadow-2xl">
            <div
              className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto"
              style={{ borderColor: THEME.primary }}
            ></div>
            <span className="text-gray-800 text-base mt-4 block font-medium">Fetching Employees Data...</span>
          </div>
        </div>
      )}

      <div className=" space-y-5">

        {/* Header Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-6 py-5">
          <h1
            className="text-xl md:text-2xl font-bold"
            style={{ color: THEME.primary }}
          >
            Employee Student Excel Data
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Track your team's performance and student promotion data
          </p>
        </div>

        {/* Filters Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div
            className="px-5 py-3 flex items-center justify-between"
            style={{ backgroundColor: THEME.primary }}
          >
            <h2 className="text-sm font-semibold text-white tracking-wide">Filters</h2>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                  <input
                    type="text"
                    placeholder="Search by name or mobile..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]"
                  />
                </div>
              </div>

              <div>
                <select
                  value={dateFilter}
                  onChange={(e) => handleDateFilterChange(e.target.value)}
                  style={{ color: THEME.primary }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#172C57] cursor-pointer font-medium"
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="custom">Custom Date</option>
                </select>
              </div>

              {dateFilter === 'custom' && (
                <div>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => handleCustomDateChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                  />
                </div>
              )}

              <div className="flex gap-2 md:col-span-1">
                <button
                  onClick={fetchTeamLeadEmployeeData}
                  className="bg-white border border-gray-300 px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap"
                  style={{ color: THEME.primary }}
                >
                  <FiRefreshCw size={13} /> Refresh
                </button>
                {(searchTerm || dateFilter !== 'today' || (dateFilter === 'custom' && selectedDate)) && (
                  <button
                    onClick={clearFilters}
                    style={{ backgroundColor: THEME.actionRed }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRedHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRed)}
                    className="text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Employees Table Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div
            className="px-5 py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-gray-200"
          >
            <div>
              <h2 className="text-sm font-semibold tracking-wide">Team Employees</h2>
              <p className=" text-xs mt-0.5">
                Total: {filteredEmployeesList.length} • Filter: {getFilterDisplayText()}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={exportAllToExcel}
                style={{ backgroundColor: THEME.actionGreen }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreenHover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreen)}
                className="text-white px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 font-semibold transition-colors whitespace-nowrap"
              >
                <FiDownload size={12} /> Export All
              </button>
              <button
                onClick={fetchTeamLeadEmployeeData}
                className="bg-white px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
                style={{ color: THEME.primary }}
              >
                <FiUsers size={12} /> Refresh
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>S.No</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Employee Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Mobile Number</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Delivered</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Read</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployeesList.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-500 text-sm">
                      <FiUsers className="text-4xl text-gray-300 mx-auto mb-2" />
                      <p>No employees found</p>
                    </td>
                  </tr>
                ) : (
                  filteredEmployeesList.map((employee, idx) => {
                    const stats = employeeStats[employee.employee_id];
                    const isLoadingStats = employeeStatsLoading[employee.employee_id];
                    const promotions = selectedEmployeePromotions[employee.employee_id] || [];
                    const isLoadingPromotions = promotionLoading[employee.employee_id];

                    return (
                      <React.Fragment key={employee.employee_id || idx}>
                        <tr className="border-b border-gray-100 hover:bg-slate-50 transition">
                          <td className="px-3 py-2 text-xs text-gray-600">{idx + 1}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-7 h-7 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: THEME.primary }}
                              >
                                <span className="text-white font-bold text-xs">
                                  {employee.employee_name?.charAt(0) || 'E'}
                                </span>
                              </div>
                              <span className="text-xs font-semibold text-gray-800">{employee.employee_name || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">{employee.employee_mobile_number || employee.mobile_number || 'N/A'}</td>
                          <td className="px-3 py-2 text-center">
                            {isLoadingStats ? (
                              <div className="flex justify-center">
                                <div
                                  className="animate-spin rounded-full h-4 w-4 border-b-2"
                                  style={{ borderColor: THEME.actionGreen }}
                                ></div>
                              </div>
                            ) : (
                              <span
                                className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 cursor-pointer hover:bg-green-200 transition"
                                onClick={() => {
                                  if (expandedRows[employee.employee_id]) setStatusFilter("Delivered");
                                  else {
                                    toggleRowExpand(employee.employee_id, employee.employee_name);
                                    setTimeout(() => setStatusFilter("Delivered"), 100);
                                  }
                                }}
                              >
                                {stats?.delivered_count || 0}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {isLoadingStats ? (
                              <div className="flex justify-center">
                                <div
                                  className="animate-spin rounded-full h-4 w-4 border-b-2"
                                  style={{ borderColor: THEME.actionBlue }}
                                ></div>
                              </div>
                            ) : (
                              <span
                                className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200 transition"
                                onClick={() => {
                                  if (expandedRows[employee.employee_id]) setStatusFilter("Read");
                                  else {
                                    toggleRowExpand(employee.employee_id, employee.employee_name);
                                    setTimeout(() => setStatusFilter("Read"), 100);
                                  }
                                }}
                              >
                                {stats?.read_count || 0}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => toggleRowExpand(employee.employee_id, employee.employee_name)}
                              style={{ color: THEME.primary }}
                              className="p-1 rounded hover:bg-slate-100 transition-colors"
                            >
                              {expandedRows[employee.employee_id] ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                            </button>
                          </td>
                        </tr>

                        {expandedRows[employee.employee_id] && (
                          <tr className="bg-slate-50">
                            <td colSpan="6" className="px-3 py-3">
                              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                <div className="bg-gray-100 px-3 py-2 flex justify-between items-center">
                                  <h3 className="font-semibold text-xs text-gray-800">
                                    Promotion Data for {employee.employee_name} — {getFilterDisplayText()}
                                  </h3>
                                  {promotions.length > 0 && (
                                    <button
                                      onClick={() => exportToExcel(promotions, employee.employee_name)}
                                      style={{ backgroundColor: THEME.actionGreen }}
                                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreenHover)}
                                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionGreen)}
                                      className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 font-semibold transition-colors whitespace-nowrap"
                                    >
                                      <FiDownload size={10} /> Export
                                    </button>
                                  )}
                                </div>
                                <div className="p-3">
                                  <PromotionTable
                                    employeeId={employee.employee_id}
                                    employeeName={employee.employee_name}
                                    promotions={promotions}
                                    isLoading={isLoadingPromotions}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Student Feedback Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Student Details"
        className="mx-auto flex flex-col my-10 max-h-[80vh] mt-[100px] max-w-lg bg-white rounded-xl shadow-xl overflow-auto outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start"
      >
        <div
          className="sticky top-0 z-10 text-white px-4 py-3 flex justify-between items-center rounded-t-xl"
          style={{ backgroundColor: THEME.primary }}
        >
          <h2 className="text-base font-semibold">Student Feedback</h2>
          <button
            onClick={closeModal}
            className="text-white hover:text-gray-200 text-2xl font-bold leading-none"
          >
            &times;
          </button>
        </div>
        <div className="p-5 text-sm">
          {selectedStudent && (
            <div className="space-y-4">
              <div className="pt-2 border-t">
                <h4
                  className="font-semibold mb-2 text-sm"
                  style={{ color: THEME.primary }}
                >
                  Full Feedback
                </h4>
                <textarea
                  value={selectedStudent.feedback || "No feedback available."}
                  readOnly
                  className="w-full h-64 p-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-800 text-sm resize-none focus:outline-none"
                />
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default TeamleadExcelPromotionDataReport;